<template>
  <div class="page">
    <SiteNav solid />
    <main id="main" class="legal" tabindex="-1">
      <header class="legal__hero">
        <div class="container">
          <router-link class="legal__back" to="/legal"
            >← Privacy &amp; terms</router-link
          >
          <p class="eyebrow">{{ content.category }}</p>
          <h1>{{ content.title }}</h1>
          <p class="legal__lede">{{ content.intro }}</p>
          <div class="legal__meta">
            <span>{{ LEGAL_STATUS }}</span>
            <span
              >Revised
              <time datetime="2026-10-01">{{ content.updated }}</time></span
            >
            <button type="button" @click="printDocument">
              Print / save PDF <span aria-hidden="true">↗</span>
            </button>
          </div>
        </div>
      </header>
      <div class="container legal__layout">
        <aside class="legal__aside">
          <details class="legal__contents" open>
            <summary>
              In this document
              <span>{{ content.sections.length }} sections</span>
            </summary>
            <nav class="legal__toc" aria-label="On this page">
              <a
                v-for="(section, i) in content.sections"
                :key="section.id"
                :href="`#${section.id}`"
              >
                <span aria-hidden="true">{{
                  String(i + 1).padStart(2, "0")
                }}</span
                >{{ section.heading }}
              </a>
            </nav>
          </details>
          <nav class="legal__switch" aria-label="Legal documents">
            <router-link
              v-for="link in legalLinks"
              :key="link.key"
              :to="link.path"
              >{{ legalDocs[link.key].title }}</router-link
            >
          </nav>
        </aside>
        <article class="legal__body" :aria-label="content.title">
          <div class="legal__summary">
            <p class="eyebrow">At a glance</p>
            <ul>
              <li v-for="item in content.summary" :key="item">{{ item }}</li>
            </ul>
            <p class="legal__summary-note">
              This summary helps you navigate. Read the full document for
              details.
            </p>
          </div>
          <div class="legal__draft" role="note">
            <strong>Publication review pending</strong>
            <p>
              Operator identity, contact verification and operational details
              still need confirmation. This is a review draft, not a finalized
              legal notice.
            </p>
          </div>
          <section
            v-for="(section, i) in content.sections"
            :id="section.id"
            :key="`${doc}-${section.id}`"
            class="legal__section"
          >
            <h2>
              <span class="legal__num" aria-hidden="true">{{
                String(i + 1).padStart(2, "0")
              }}</span
              >{{ section.heading }}
            </h2>
            <p v-for="(paragraph, j) in section.paragraphs" :key="`p${j}`">
              {{ paragraph }}
            </p>
            <ul v-if="section.list">
              <li v-for="item in section.list" :key="item">{{ item }}</li>
            </ul>
            <div
              v-if="section.table"
              class="legal__table-wrap"
              tabindex="0"
              role="region"
              :aria-label="`${section.heading} table; scroll horizontally on small screens`"
            >
              <table>
                <caption class="sr-only">
                  {{
                    section.heading
                  }}
                </caption>
                <thead>
                  <tr>
                    <th
                      v-for="heading in section.table.headers"
                      :key="heading"
                      scope="col"
                    >
                      {{ heading }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, j) in section.table.rows" :key="j">
                    <th scope="row">{{ row[0] }}</th>
                    <td v-for="(cell, k) in row.slice(1)" :key="k">
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="section.links" class="legal__links">
              <template v-for="link in section.links" :key="link.href">
                <router-link v-if="link.href.startsWith('/')" :to="link.href"
                  >{{ link.label }}
                  <span aria-hidden="true">→</span></router-link
                >
                <a v-else :href="link.href"
                  >{{ link.label }} <span aria-hidden="true">↗</span></a
                >
              </template>
            </div>
          </section>
          <div class="legal__help">
            <p class="eyebrow">A question about your account?</p>
            <h2>Let’s make it clear.</h2>
            <a :href="`mailto:${SUPPORT_EMAIL}`"
              >{{ SUPPORT_EMAIL }} <span aria-hidden="true">↗</span></a
            ><a href="#main">Back to top ↑</a>
          </div>
        </article>
      </div>
    </main>
    <SiteFooter />
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import SiteNav from "@/components/site/SiteNav.vue";
import SiteFooter from "@/components/site/SiteFooter.vue";
import {
  legalDocs,
  legalLinks,
  LEGAL_STATUS,
  LegalDocKey,
} from "@/content/legal";
import { SUPPORT_EMAIL } from "@/content/links";
const props = defineProps<{ doc: LegalDocKey }>();
const content = computed(() => legalDocs[props.doc]);
const printDocument = () => window.print();
</script>
<style scoped>
.legal {
  padding-top: var(--nav-h);
}
.legal__hero {
  background: #f1eff8;
  border-bottom: 1px solid #ded9ed;
  padding: 48px 0 56px;
}
.legal__back {
  display: inline-block;
  color: #6551a5;
  font-size: 14px;
  margin-bottom: 40px;
  padding: 8px 0;
}
.legal__hero .eyebrow {
  color: #6551a5;
}
h1 {
  font-family: var(--font-display);
  font-size: clamp(38px, 5.5vw, 72px);
  font-weight: 500;
  line-height: 1.08;
  letter-spacing: -0.05em;
  max-width: 1000px;
  margin: 18px 0 24px;
  text-wrap: balance;
}
.legal__lede {
  max-width: 740px;
  font-size: clamp(17px, 2vw, 21px);
  color: var(--text-secondary);
  line-height: 1.65;
}
.legal__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 28px;
  margin-top: 32px;
  font-size: 13px;
  color: var(--text-secondary);
}
.legal__meta > span:first-child {
  background: #e5dff5;
  color: #51417e;
  border: 1px solid #d2c7e8;
  border-radius: 30px;
  padding: 5px 12px;
}
.legal__meta button {
  cursor: pointer;
  border: 0;
  border-bottom: 1px solid #b5aacd;
  padding: 8px 0;
  background: transparent;
}
.legal__layout {
  display: grid;
  grid-template-columns: minmax(220px, 3fr) minmax(0, 8fr);
  gap: 64px;
  padding-top: 64px;
  padding-bottom: 88px;
}
.legal__aside {
  position: sticky;
  top: calc(var(--nav-h) + 24px);
  align-self: start;
  max-height: calc(100vh - var(--nav-h) - 48px);
  overflow-y: auto;
  padding: 0 8px 12px 0;
  scrollbar-width: thin;
}
.legal__contents summary {
  font-size: 13px;
  font-weight: 600;
  list-style: none;
}
.legal__contents summary span {
  display: block;
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 400;
  margin-top: 4px;
}
.legal__toc {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 18px;
}
.legal__toc a {
  display: flex;
  gap: 12px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--text-secondary);
  padding: 9px 4px;
  border-radius: 6px;
}
.legal__toc a span {
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}
.legal__toc a:hover {
  background: #f1eff8;
  color: #51417e;
}
.legal__switch {
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border-medium);
}
.legal__switch a {
  padding: 9px 0;
  font-size: 12px;
  color: var(--text-secondary);
}
.legal__switch .router-link-exact-active {
  color: #6551a5;
  font-weight: 600;
}
.legal__body {
  min-width: 0;
  max-width: 850px;
}
.legal__summary {
  background: #17182d;
  color: #fff;
  padding: 32px 36px;
  border-radius: 16px;
}
.legal__summary .eyebrow {
  color: #c1bbff;
}
.legal__summary ul {
  padding-left: 20px;
  margin: 20px 0;
}
.legal__summary li + li {
  margin-top: 14px;
}
.legal__summary-note {
  font-size: 12px;
  color: #c7c6d9;
}
.legal__draft {
  padding: 24px 0 32px;
  color: var(--text-secondary);
  font-size: 13px;
}
.legal__draft strong {
  color: #51417e;
  font-weight: 600;
}
.legal__draft p {
  margin-top: 8px;
}
.legal__section {
  padding: 36px 0;
  border-top: 1px solid var(--border-medium);
  scroll-margin-top: calc(var(--nav-h) + 24px);
}
.legal__section h2 {
  display: flex;
  align-items: baseline;
  gap: 16px;
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 25px;
  letter-spacing: -0.025em;
  line-height: 1.3;
  margin-bottom: 24px;
}
.legal__num {
  font-family: var(--font);
  font-size: 12px;
  color: #6551a5;
  font-variant-numeric: tabular-nums;
}
.legal__section p,
.legal__section li {
  color: var(--text-secondary);
  line-height: 1.85;
  font-size: 15px;
  overflow-wrap: anywhere;
}
.legal__section p + p,
.legal__section p + ul,
.legal__section ul + p {
  margin-top: 18px;
}
.legal__section ul {
  margin: 0;
  padding-left: 20px;
}
.legal__section li {
  padding-left: 6px;
}
.legal__section li + li {
  margin-top: 14px;
}
.legal__section li::marker {
  color: #6551a5;
}
.legal__table-wrap {
  overflow-x: auto;
  margin: 24px 0;
  border: 1px solid #ded9ed;
  border-radius: 10px;
}
table {
  border-collapse: collapse;
  width: 100%;
  min-width: 520px;
  text-align: left;
  font-size: 13px;
  line-height: 1.7;
}
th,
td {
  padding: 18px;
  vertical-align: top;
  border-bottom: 1px solid #e7e3f0;
}
thead th {
  background: #f1eff8;
  font-weight: 600;
  color: #51417e;
}
tbody th {
  width: 24%;
  font-weight: 500;
}
td {
  color: var(--text-secondary);
}
tr:last-child td,
tr:last-child th {
  border-bottom: 0;
}
.legal__links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  margin-top: 24px;
}
.legal__links a,
.legal__help a {
  font-size: 14px;
  color: #6551a5;
  text-decoration: underline;
  text-underline-offset: 5px;
  padding: 8px 0;
  overflow-wrap: anywhere;
}
.legal__help {
  padding: 32px;
  border-radius: 16px;
  background: #f1eff8;
  margin-top: 24px;
}
.legal__help h2 {
  margin: 12px 0;
  font-size: 30px;
  font-weight: 500;
  letter-spacing: -0.035em;
}
.legal__help a {
  display: block;
  width: fit-content;
}
@media (max-width: 1000px) {
  .legal__layout {
    gap: 32px;
    grid-template-columns: 210px minmax(0, 1fr);
  }
}
@media (max-width: 767px) {
  .legal__hero {
    padding: 28px 0 36px;
  }
  .legal__back {
    margin-bottom: 24px;
  }
  .legal__layout {
    grid-template-columns: 1fr;
    gap: 32px;
    padding-top: 32px;
  }
  .legal__aside {
    position: static;
    max-height: none;
    padding: 0;
  }
  .legal__contents {
    border: 1px solid #ded9ed;
    border-radius: 12px;
    padding: 20px;
  }
  .legal__contents summary {
    cursor: pointer;
    list-style: revert;
  }
  .legal__toc {
    max-height: 260px;
    overflow-y: auto;
  }
  .legal__toc a {
    padding: 12px 0;
  }
  .legal__switch {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px 20px;
  }
  .legal__summary {
    padding: 24px;
  }
  .legal__section h2 {
    font-size: 22px;
  }
}
@media print {
  .legal {
    padding: 0;
  }
  .legal__hero {
    background: none;
    padding: 0 0 24px;
  }
  .legal__back,
  .legal__meta button,
  .legal__aside,
  .legal__help {
    display: none;
  }
  .legal__layout {
    display: block;
    padding-top: 24px;
  }
  .legal__summary {
    background: none;
    color: #000;
    border: 1px solid #aaa;
  }
  .legal__summary .eyebrow,
  .legal__summary-note {
    color: #333;
  }
  .legal__section {
    padding: 20px 0;
  }
  h2 {
    break-after: avoid;
  }
  tr {
    break-inside: avoid;
  }
  .legal__table-wrap {
    overflow: visible;
  }
  table {
    min-width: 0;
  }
}
</style>
